import React, { useState } from 'react';
import { Navigation } from '../components/Navigation';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { motion, AnimatePresence } from 'framer-motion';
import { BrainCircuit, Check, MessageCircle, User, X, Send, Phone, Mail, MapPin } from 'lucide-react';
const matches = [{
  id: 1,
  name: 'David O.',
  age: 21,
  dept: 'Computer Science',
  match: 95,
  tags: ['Night Owl', 'Gamer', 'Quiet'],
  image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  bio: 'Final year CS student. Love gaming and coding late at night. Looking for someone who respects quiet study time.',
  location: 'Unilag, Akoka',
  phone: '+234 803 456 7890',
  email: 'david@student.edu.ng'
}, {
  id: 2,
  name: 'Sarah K.',
  age: 20,
  dept: 'Mass Comm',
  match: 88,
  tags: ['Social', 'Tidy', 'Cooks'],
  image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  bio: 'Love cooking and keeping things organized. Social but also respect personal space. Looking for a clean roommate!',
  location: 'Yaba Tech, Yaba',
  phone: '+234 805 123 4567',
  email: 'sarah@student.edu.ng'
}, {
  id: 3,
  name: 'Emmanuel J.',
  age: 22,
  dept: 'Engineering',
  match: 82,
  tags: ['Studious', 'Early Bird', 'Gym'],
  image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  bio: 'Engineering student who loves early morning workouts. Serious about studies but also know how to have fun on weekends.',
  location: 'LUTH, Idi-Araba',
  phone: '+234 807 890 1234',
  email: 'emmanuel@student.edu.ng'
}];
export function RoommateMatchPage() {
  const [step, setStep] = useState<'intro' | 'quiz' | 'results'>('intro');
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [selectedMatch, setSelectedMatch] = useState<(typeof matches)[0] | null>(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState<Array<{
    id: number;
    text: string;
    sender: 'me' | 'them';
    time: string;
  }>>([{
    id: 1,
    text: "Hi! I saw we're a great match. Would love to chat about potentially being roommates!",
    sender: 'me',
    time: '10:30 AM'
  }, {
    id: 2,
    text: "Hey! Yes, I'd love to discuss that. What hostel are you looking at?",
    sender: 'them',
    time: '10:32 AM'
  }]);
  const [newMessage, setNewMessage] = useState('');
  const questions = [{
    id: 'sleep',
    label: "What's your typical sleep schedule?",
    options: ['Early Bird (10pm)', 'Night Owl (2am)', 'Flexible']
  }, {
    id: 'clean',
    label: 'How clean are you?',
    options: ['Spick & Span', 'Reasonably Tidy', 'Organized Chaos']
  }, {
    id: 'guests',
    label: 'How do you feel about guests?',
    options: ['Love them', 'Occasional is fine', 'Prefer privacy']
  }, {
    id: 'budget',
    label: 'Monthly budget for rent?',
    options: ['< ₦50k', '₦50k - ₦100k', '> ₦100k']
  }];
  const sendMessage = () => {
    if (newMessage.trim()) {
      const newMsg = {
        id: messages.length + 1,
        text: newMessage,
        sender: 'me' as const,
        time: new Date().toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit'
        })
      };
      setMessages([...messages, newMsg]);
      setNewMessage('');
      setTimeout(() => {
        const response = {
          id: messages.length + 2,
          text: "That sounds great! Let me know when you'd like to meet up.",
          sender: 'them' as const,
          time: new Date().toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit'
          })
        };
        setMessages(prev => [...prev, response]);
      }, 2000);
    }
  };
  return <div className="min-h-screen bg-slate-50 pt-20" data-id="element-965" data-mp="16dbes5ywsy07">
      <Navigation data-id="element-966" data-mp="11eqbfxod1y7j" />

      {/* Chat Modal */}
      <AnimatePresence data-id="element-967" data-mp="11buby4wv71gz">
        {chatOpen && selectedMatch && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setChatOpen(false)} data-id="element-968" data-mp="1z0nv3yeh89l2">
            <motion.div initial={{
          scale: 0.9,
          y: 20
        }} animate={{
          scale: 1,
          y: 0
        }} exit={{
          scale: 0.9,
          y: 20
        }} className="bg-white rounded-2xl max-w-2xl w-full h-[600px] flex flex-col" onClick={e => e.stopPropagation()} data-id="element-969" data-mp="domj4mgmhkvu">
              <div className="flex items-center justify-between p-4 border-b border-slate-200" data-id="element-970" data-mp="2u8puz8ku5nxc">
                <div className="flex items-center gap-3" data-id="element-971" data-mp="2z3m0nbizw9sx">
                  <img src={selectedMatch.image} alt={selectedMatch.name} className="w-10 h-10 rounded-full object-cover" data-id="element-972" data-mp="33zkishdqcvkw" />
                  <div data-id="element-973" data-mp="3d2bxcq1wk7gr">
                    <h3 className="font-bold text-brand-dark" data-id="element-974" data-mp="3d1tw1f0dv31z">
                      {selectedMatch.name}
                    </h3>
                    <p className="text-xs text-emerald-600" data-id="element-975" data-mp="1in9c54jvzbjn">● Online</p>
                  </div>
                </div>
                <button onClick={() => setChatOpen(false)} className="p-2 hover:bg-slate-100 rounded-full transition-colors" data-id="element-976" data-mp="1nm47t72ycul6">
                  <X className="w-5 h-5" data-id="element-977" data-mp="1fnhjhy6gwq1x" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-978" data-mp="1bm5vty75mewd">
                {messages.map(msg => <div key={msg.id} className={`flex ${msg.sender === 'me' ? 'justify-end' : 'justify-start'}`} data-id="element-979" data-mp="1kkr85bm18xop">
                    <div className={`max-w-[70%] ${msg.sender === 'me' ? 'order-2' : 'order-1'}`} data-id="element-980" data-mp="1yipmih0csu47">
                      <div className={`rounded-2xl px-4 py-2 ${msg.sender === 'me' ? 'bg-brand-primary text-white' : 'bg-slate-100 text-slate-900'}`} data-id="element-981" data-mp="28a2a2wf4cy4f">
                        <p className="text-sm" data-id="element-982" data-mp="2i89op8lxr439">{msg.text}</p>
                      </div>
                      <p className="text-xs text-slate-400 mt-1 px-2" data-id="element-983" data-mp="2d9evdcqcdx4w">
                        {msg.time}
                      </p>
                    </div>
                  </div>)}
              </div>

              <div className="p-4 border-t border-slate-200" data-id="element-984" data-mp="30xbsrvj7794u">
                <div className="flex gap-2" data-id="element-985" data-mp="30uh9w8fu1wdj">
                  <input type="text" value={newMessage} onChange={e => setNewMessage(e.target.value)} onKeyPress={e => e.key === 'Enter' && sendMessage()} placeholder="Type a message..." className="flex-1 px-4 py-2 border border-slate-300 rounded-full focus:ring-2 focus:ring-brand-primary focus:outline-none" data-id="element-986" data-mp="2mxu37dy2vruf" />
                  <button onClick={sendMessage} className="w-10 h-10 bg-brand-primary hover:bg-brand-light text-white rounded-full flex items-center justify-center transition-colors" data-id="element-987" data-mp="244q257ij6cps">
                    <Send className="w-5 h-5" data-id="element-988" data-mp="1z7pzz7osxu3y" />
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Profile Detail Modal */}
      <AnimatePresence data-id="element-989" data-mp="2aoe1miskf6op">
        {selectedMatch && !chatOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setSelectedMatch(null)} data-id="element-990" data-mp="25o03ptkohd4e">
            <motion.div initial={{
          scale: 0.9,
          y: 20
        }} animate={{
          scale: 1,
          y: 0
        }} exit={{
          scale: 0.9,
          y: 20
        }} className="bg-white rounded-2xl max-w-lg w-full" onClick={e => e.stopPropagation()} data-id="element-991" data-mp="1mxs5npvu5cv2">
              <div className="relative" data-id="element-992" data-mp="xw2n56nlr5k8">
                <img src={selectedMatch.image} alt={selectedMatch.name} className="w-full h-64 object-cover rounded-t-2xl" data-id="element-993" data-mp="xzgn688sn2oi" />
                <button onClick={() => setSelectedMatch(null)} className="absolute top-4 right-4 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center transition-colors" data-id="element-994" data-mp="17t76yztz6oxv">
                  <X className="w-5 h-5" data-id="element-995" data-mp="k6r1k4x6fzrx" />
                </button>
                <div className="absolute bottom-4 left-4 right-4" data-id="element-996" data-mp="p52amdfne693">
                  <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4" data-id="element-997" data-mp="p3hc6cq7q35y">
                    <h3 className="text-2xl font-bold text-brand-dark" data-id="element-998" data-mp="tx24bo54k2x8">
                      {selectedMatch.name}
                    </h3>
                    <p className="text-slate-600" data-id="element-999" data-mp="1f7zq1ew2rrh">
                      {selectedMatch.age} • {selectedMatch.dept}
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6" data-id="element-1000" data-mp="307fcm363l2u">
                <div className="flex items-center justify-center gap-2 mb-6" data-id="element-1001" data-mp="2z4ryo5200ro">
                  <div className="text-4xl font-bold text-emerald-600" data-id="element-1002" data-mp="7yr5yzsphl1j">
                    {selectedMatch.match}%
                  </div>
                  <div className="text-sm text-slate-500" data-id="element-1003" data-mp="c0cde5mh174c">Match Score</div>
                </div>

                <div className="flex flex-wrap gap-2 justify-center mb-6" data-id="element-1004" data-mp="3bhke2x2pthob">
                  {selectedMatch.tags.map(tag => <Badge key={tag} variant="neutral" data-id="element-1005" data-mp="3gcpx9o4qgi8p">
                      {tag}
                    </Badge>)}
                </div>

                <div className="bg-slate-50 rounded-xl p-4 mb-6" data-id="element-1006" data-mp="3p87dkrgavblg">
                  <h4 className="font-bold text-brand-dark mb-2" data-id="element-1007" data-mp="2spro7biy0ckt">About</h4>
                  <p className="text-slate-600 text-sm leading-relaxed" data-id="element-1008" data-mp="2snovukubi7cx">
                    {selectedMatch.bio}
                  </p>
                </div>

                <div className="space-y-3 mb-6" data-id="element-1009" data-mp="2p6ao5x7nmo26">
                  <div className="flex items-center gap-2 text-slate-600" data-id="element-1010" data-mp="2k8hid5a9jw2f">
                    <MapPin className="w-4 h-4" data-id="element-1011" data-mp="2k6eiltjb6oki" />
                    <span className="text-sm" data-id="element-1012" data-mp="2g64e6lb6067r">{selectedMatch.location}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600" data-id="element-1013" data-mp="2b8m2wo9odewl">
                    <Phone className="w-4 h-4" data-id="element-1014" data-mp="268q02scprtim" />
                    <span className="text-sm" data-id="element-1015" data-mp="269hrdqza3533">{selectedMatch.phone}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600" data-id="element-1016" data-mp="21h7xy0yyfmw2">
                    <Mail className="w-4 h-4" data-id="element-1017" data-mp="1wi53fagloz2c" />
                    <span className="text-sm" data-id="element-1018" data-mp="1wgtyv8fbzna6">{selectedMatch.email}</span>
                  </div>
                </div>

                <Button className="w-full" onClick={() => {
              setChatOpen(true);
            }} data-id="element-1019" data-mp="1ni8injxc0kck">
                  <MessageCircle className="w-4 h-4 mr-2" data-id="element-1020" data-mp="1ijx6v3nh7zrp" />
                  Start Chat
                </Button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      <Section data-id="element-1021" data-mp="1ds9opbh9dkdc">
        <div className="max-w-6xl mx-auto" data-id="element-1022" data-mp="yxdc6m2f2aug">
          <AnimatePresence mode="wait" data-id="element-1023" data-mp="yyn9h91g3f01">
            {step === 'intro' && <motion.div key="intro" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-1024" data-mp="13vn66tcen5eh">
                {/* Left: Text Content */}
                <div className="text-center lg:text-left" data-id="element-1025" data-mp="2akdd8m8l18ck">
                  <div className="w-20 h-20 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center mx-auto lg:mx-0 mb-6" data-id="element-1026" data-mp="2aisiowm9ppep">
                    <BrainCircuit className="w-10 h-10" data-id="element-1027" data-mp="3vurazw5yf4g" />
                  </div>
                  <h1 className="text-4xl font-bold text-brand-dark mb-4" data-id="element-1028" data-mp="3v3799h4vewlf">
                    Find Your Vibe Tribe
                  </h1>
                  <p className="text-xl text-slate-600 mb-8" data-id="element-1029" data-mp="dl4naqu4ohxu">
                    Stop living with strangers who clash with your lifestyle.
                    Take our 2-minute "Vibe Check" to find compatible roommates.
                  </p>
                  <Button size="lg" onClick={() => setStep('quiz')} data-id="element-1030" data-mp="3c41qmmnm8j40">
                    Start Vibe Check
                  </Button>
                </div>

                {/* Right: Illustration */}
                <motion.div initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: 0.2,
              duration: 0.6
            }} className="relative" data-id="element-1031" data-mp="3m2958yufmp2u">
                  <img src="https://cdn.magicpatterns.com/uploads/42YNqY8a5G5zatBrPUmek9/Copy_of_Billvast_Shirt_Mock-up_(2).png" alt="AI Roommate Matching" className="w-full h-auto max-w-md mx-auto drop-shadow-2xl" data-id="element-1032" data-mp="20n0ieemd29wp" />
                  <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-4 shadow-xl" data-id="element-1033" data-mp="2flkp85eh29vd">
                    <div className="text-sm text-slate-500 mb-1" data-id="element-1034" data-mp="2jluwrmhwx8cc">
                      AI-Powered Matching
                    </div>
                    <div className="text-2xl font-bold text-brand-primary" data-id="element-1035" data-mp="2odwg1sdhzzbv">
                      95% Success Rate
                    </div>
                  </div>
                </motion.div>
              </motion.div>}

            {step === 'quiz' && <motion.div key="quiz" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} data-id="element-1036" data-mp="339ua953v2tlg">
                <Card data-id="element-1037" data-mp="cq13rbb01gwf">
                  <CardContent className="p-8" data-id="element-1038" data-mp="cqsykjwrvaln">
                    <h2 className="text-2xl font-bold mb-6" data-id="element-1039" data-mp="cs41yimb2gc8">
                      Tell us about yourself
                    </h2>
                    <div className="space-y-8" data-id="element-1040" data-mp="15eykvb3h57nl">
                      {questions.map(q => <div key={q.id} data-id="element-1041" data-mp="10nowedtnwada">
                          <label className="block text-lg font-medium text-brand-dark mb-3" data-id="element-1042" data-mp="10mnk660wthtz">
                            {q.label}
                          </label>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-1043" data-mp="1t3zoml1d3umj">
                            {q.options.map(opt => <button key={opt} onClick={() => setAnswers({
                        ...answers,
                        [q.id]: opt
                      })} className={`p-3 rounded-lg border text-sm font-medium transition-all ${answers[q.id] === opt ? 'border-indigo-500 bg-indigo-50 text-indigo-700 ring-1 ring-indigo-500' : 'border-slate-200 hover:border-indigo-300 hover:bg-slate-50'}`} data-id="element-1044" data-mp="1ocre1loaq37s">
                                {opt}
                              </button>)}
                          </div>
                        </div>)}
                    </div>
                    <div className="mt-8 flex justify-end" data-id="element-1045" data-mp="o5m8qpy1ogn0">
                      <Button onClick={() => setStep('results')} disabled={Object.keys(answers).length < questions.length} data-id="element-1046" data-mp="o65nxywb8qxc">
                        Find Matches
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>}

            {step === 'results' && <motion.div key="results" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} data-id="element-1047" data-mp="2ikt16qh9iwub">
                <div className="flex justify-between items-center mb-6" data-id="element-1048" data-mp="2rdmy01sr6evx">
                  <h2 className="text-2xl font-bold text-brand-dark" data-id="element-1049" data-mp="369uf2t8v706z">
                    We found {matches.length} potential matches!
                  </h2>
                  <Button variant="outline" size="sm" onClick={() => setStep('quiz')} data-id="element-1050" data-mp="1bwusuh9zb7qq">
                    Retake Quiz
                  </Button>
                </div>

                <div className="grid gap-4" data-id="element-1051" data-mp="l42ozfl0d3a1">
                  {matches.map((match, idx) => <motion.div key={idx} initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: idx * 0.1
              }} data-id="element-1052" data-mp="q2nr5kjtlx14" data-mpi={idx}>
                      <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setSelectedMatch(match)} data-id="element-1053" data-mp="76xnlsw8osl4">
                        <CardContent className="flex flex-col sm:flex-row items-center gap-6 p-6" data-id="element-1054" data-mp="g3g7ygqen863">
                          <img src={match.image} alt={match.name} className="w-24 h-24 rounded-full object-cover ring-4 ring-slate-100" data-id="element-1055" data-mp="1rky0ek2jcldb" />

                          <div className="flex-1 text-center sm:text-left" data-id="element-1056" data-mp="382omf93gj7sv">
                            <div className="flex items-center justify-center sm:justify-start gap-2 mb-1" data-id="element-1057" data-mp="33a5ccm3zaust">
                              <h3 className="text-xl font-bold text-brand-dark" data-id="element-1058" data-mp="2ycdhbj81eske">
                                {match.name}
                              </h3>
                              <span className="text-sm text-slate-500" data-id="element-1059" data-mp="2ta4piq6whwjv">
                                ({match.age})
                              </span>
                            </div>
                            <p className="text-slate-600 mb-2" data-id="element-1060" data-mp="2kdx3l8brc1sr">{match.dept}</p>
                            <div className="flex flex-wrap justify-center sm:justify-start gap-2" data-id="element-1061" data-mp="2feaqcyvxiljy">
                              {match.tags.map(tag => <Badge key={tag} variant="neutral" data-id="element-1062" data-mp="dktf7l8lb2kg">
                                  {tag}
                                </Badge>)}
                            </div>
                          </div>

                          <div className="text-center" data-id="element-1063" data-mp="11xmmgcrkum35">
                            <div className="text-3xl font-bold text-emerald-600 mb-1" data-id="element-1064" data-mp="x5cqog3s6rsy">
                              {match.match}%
                            </div>
                            <div className="text-xs text-slate-500 uppercase tracking-wide font-semibold mb-3" data-id="element-1065" data-mp="s572j9wpznfm">
                              Match Score
                            </div>
                            <Button size="sm" onClick={e => {
                        e.stopPropagation();
                        setSelectedMatch(match);
                        setChatOpen(true);
                      }} data-id="element-1066" data-mp="j8fyaa80l29y">
                              <MessageCircle className="w-4 h-4 mr-2" data-id="element-1067" data-mp="23lx6emdo8362" />
                              Chat
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>)}
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </Section>
    </div>;
}